import { useState } from "react";
import { Badge, BlockStack, DatePicker, Modal, Text } from "@shopify/polaris";
import { patchRequest } from "../../../shared/components/functions";
import { Toasts } from "../../../shared/components/toasts/toasts";

const parseDeliveryTerm = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const input = String(value).trim();
  if (!input) return null;

  const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (isoMatch) return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]), 12);

  const slashMatch = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) return new Date(Number(slashMatch[3]), Number(slashMatch[2]) - 1, Number(slashMatch[1]), 12);

  const dashMatch = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dashMatch) return new Date(Number(dashMatch[3]), Number(dashMatch[2]) - 1, Number(dashMatch[1]), 12);

  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12);
};

const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function UpdateDeliveryTermModal({ order_id, deliveryTerm, callback, tone }) {
  const initialDate = parseDeliveryTerm(deliveryTerm) || new Date();

  const [active, setActive] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [{ month, year }, setDate] = useState({ month: initialDate.getMonth(), year: initialDate.getFullYear() });
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const openModal = () => {
    const nextDate = parseDeliveryTerm(deliveryTerm) || new Date();
    setSelectedDate(nextDate);
    setDate({ month: nextDate.getMonth(), year: nextDate.getFullYear() });
    setActive(true);
  };

  const onClose = () => {
    setActive(false);
  };

  const handleMonthChange = (nextMonth, nextYear) => setDate({ month: nextMonth, year: nextYear });

  const handleDateChange = (dates) => {
    const nextDate = dates.start || dates;
    if (nextDate instanceof Date) setSelectedDate(nextDate);
  };

  const onConfirm = async () => {
    setIsSaving(true);
    await patchRequest(`/api/sales/${order_id}`, { delivery_term: formatDateForApi(selectedDate) }, setToast);
    setIsSaving(false);
    setActive(false);
    if (callback) await callback();
  };

  return (
    <>
      <Toasts toast={toast} setToast={setToast} toastContent="Delivery term updated" />
      <button
        type="button"
        onClick={openModal}
        style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
        title="Update delivery term">
        <Badge tone={tone}>{deliveryTerm || "No date"}</Badge>
      </button>

      <Modal
        open={active}
        onClose={onClose}
        title="Update delivery term"
        primaryAction={{
          content: "Save",
          onAction: onConfirm,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: onClose,
          },
        ]}>
        <Modal.Section>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd" fontWeight="medium">
                Internal update only.
              </Text>
              <Text as="p" variant="bodyMd">
                No email will be sent to the customer. This only updates the delivery term used internally to manage fulfilment priority.
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {`Current delivery term: ${deliveryTerm || "No date set"}`}
              </Text>
            </BlockStack>

            <DatePicker month={month} year={year} onChange={handleDateChange} onMonthChange={handleMonthChange} selected={selectedDate} />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
